import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface BranchContextType {
  selectedBranch: number | null;
  setSelectedBranch: (branchId: number | null) => void;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

const SELECTED_BRANCH_STORAGE_KEY = 'selectedBranchId';

interface BranchProviderProps {
  children: ReactNode;
}

export function BranchProvider({ children }: BranchProviderProps) {
  // Load selectedBranch from localStorage on mount
  const [selectedBranch, setSelectedBranchState] = useState<number | null>(() => {
    const stored = localStorage.getItem(SELECTED_BRANCH_STORAGE_KEY);
    if (!stored) return null;
    const parsed = parseInt(stored, 10);
    return Number.isNaN(parsed) ? null : parsed;
  });

  // Persist selectedBranch to localStorage whenever it changes
  useEffect(() => {
    if (selectedBranch !== null) {
      localStorage.setItem(SELECTED_BRANCH_STORAGE_KEY, selectedBranch.toString());
    } else {
      localStorage.removeItem(SELECTED_BRANCH_STORAGE_KEY);
    }
  }, [selectedBranch]);

  const setSelectedBranch = (branchId: number | null) => {
    setSelectedBranchState(branchId);
  };

  return (
    <BranchContext.Provider value={{ selectedBranch, setSelectedBranch }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
}
